import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    IconButton,
    CircularProgress
} from '@mui/material';
import {
    CloseIcon,
    EmailOutlinedIcon,
    EmailIcon
} from 'icons/materialIcons';
import { sendLeadEmail } from 'apis/emailApi';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamSendLeadEmailModal = ({ open, onClose, lead, contact }) => {
    const leadData = lead || contact; // Support both prop names for compatibility
    const [emailData, setEmailData] = useState({
        subject: '',
        message: ''
    });
    const [isSending, setIsSending] = useState(false);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleInputChange = (field, value) => {
        setEmailData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!emailData.subject.trim()) {
            newErrors.subject = 'Subject is required';
        }

        if (!emailData.message.trim()) {
            newErrors.message = 'Message is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSend = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSending(true);

        try {
            const emailPayload = {
                to: leadData?.leadEmail,
                subject: emailData.subject,
                message: emailData.message,
                leadId: leadData?._id
            };

            console.log('Sending email to:', leadData?.leadEmail);
            console.log('Email data:', emailPayload);

            // Call real API to send lead email
            await sendLeadEmail(emailPayload);

            // Show success notification
            setSnackbar({
                open: true,
                message: 'Email sent successfully',
                severity: 'success'
            });

            // Reset form and close modal
            setEmailData({ subject: '', message: '' });

            setTimeout(() => {
                onClose();
            }, 1000);
        } catch (error) {
            console.error('Error sending email:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to send email. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsSending(false);
        }
    };

    const handleClose = () => {
        setEmailData({ subject: '', message: '' });
        setErrors({});
        onClose();
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        padding: 2
                    }
                }}
            >
                <DialogTitle sx={{ padding: 0, marginBottom: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    backgroundColor: '#e3f2fd',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <EmailOutlinedIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333333' }}>
                                    Send Email
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#666666' }}>
                                    To: {leadData?.leadEmail || 'Unknown Email'}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            onClick={handleClose}
                            sx={{
                                color: '#666666',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ padding: 0, marginBottom: 2 }}>
                    <Box sx={{ marginBottom: 3 }}>
                        <TextField
                            fullWidth
                            label="Subject"
                            value={emailData.subject}
                            onChange={(e) => handleInputChange('subject', e.target.value)}
                            error={!!errors.subject}
                            helperText={errors.subject}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                }
                            }}
                        />
                    </Box>

                    <TextField
                        fullWidth
                        label="Message"
                        multiline
                        rows={8}
                        value={emailData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        error={!!errors.message}
                        helperText={errors.message}
                        placeholder="Type your message here..."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            }
                        }}
                    />
                </DialogContent>

                <DialogActions sx={{ padding: 0, gap: 1 }}>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        sx={{
                            borderColor: '#e0e0e0',
                            color: '#666666',
                            textTransform: 'none',
                            borderRadius: '8px',
                            paddingX: 3,
                            '&:hover': {
                                borderColor: '#1976d2',
                                color: '#1976d2',
                                backgroundColor: '#f3f8ff'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSend}
                        variant="contained"
                        disabled={isSending}
                        startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
                        sx={{
                            backgroundColor: '#1976d2',
                            textTransform: 'none',
                            borderRadius: '8px',
                            paddingX: 3,
                            '&:hover': {
                                backgroundColor: '#1565c0'
                            }
                        }}
                    >
                        {isSending ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleSnackbarClose}
            />
        </>
    );
};

export default TeamSendLeadEmailModal;
